/*
 * @Copyright 2026. Institute for Future Intelligence, Inc.
 *
 * A small floating progress card shown while a 3D-print export is converting the
 * rendered scene to a printable file. A large protein can expand into millions
 * of triangles, so the conversion (geometry build + file encoding) is chunked
 * and yields to the UI; this overlay reflects `usePrimitiveStore.exportProgress`,
 * which those chunks update. Rendered globally so it covers BOTH export paths —
 * the gallery-card export (driven by ExportModelModal) and the active-lab export
 * (driven by useExportRenderedHandler).
 */

import React from 'react';
import { Progress } from 'antd';
import { useTranslation } from 'react-i18next';
import { usePrimitiveStore } from '../stores/commonPrimitive.ts';
import { useStore } from '../stores/common.ts';
import * as Selector from '../stores/selector';

const ExportProgressOverlay = React.memo(() => {
  const progress = usePrimitiveStore(Selector.exportProgress);
  const language = useStore(Selector.language);
  const { t } = useTranslation();

  if (!progress) return null;
  const lang = { lng: language };
  const phaseLabel = t(`exportModel.${progress.phase}`, lang);

  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        zIndex: 10000,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'rgba(0, 0, 0, 0.25)',
        pointerEvents: 'all',
      }}
    >
      <div
        style={{
          width: 320,
          padding: '18px 22px',
          borderRadius: 8,
          background: '#fff',
          boxShadow: '0 4px 24px rgba(0, 0, 0, 0.25)',
          textAlign: 'center',
        }}
      >
        <div style={{ fontWeight: 600, marginBottom: 4 }}>{t('exportModel.PreparingPrint', lang)}</div>
        <div style={{ color: '#888', fontSize: 12, marginBottom: 12 }}>{phaseLabel}</div>
        <Progress percent={progress.percent} status="active" />
      </div>
    </div>
  );
});

export default ExportProgressOverlay;
